import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Table, Spinner } from 'react-bootstrap';
import classes from './organizationsList.module.scss';
import { StandardButton } from '../../../common/Button';
import {
  getUsersOrganizations,
  deleteOrganization,
  updateUsersOrganization,
} from '../../../../api/organization';
import { getOrganizationsQueues } from '../../../../api/queue';
import { onAuthStateChanged } from '@firebase/auth';
import { auth } from '../../../../FirebaseConfig';
import { FaInbox } from 'react-icons/fa';
import { useNavigate, NavLink } from 'react-router-dom';
import FileUploader from './FileUploader';
import { MdDomainAdd } from "react-icons/md";
import { Menu, MenuItem, IconButton, Avatar } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import ModalConfirm from '../../../common/ModalConfirm/ModalConfirm';
import Toast from '../../../common/Toast';

function OrganizationsList() {
  const [userUid, setUserUid] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [orgList, setOrgList] = useState([]);
  const [ifEditMode, setIfEditMode] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [colorInput, setColorInput] = useState('#fff');
  const [queuesCounts, setQueuesCounts] = useState({});
  const [enableImgUpload, setEnableImgUpload] = useState(false);
  const [logo, setLogo] = useState('');
  const [menuAnchor, setMenuAnchor] = useState({});
  const [loading, setLoading] = useState(true);  // Loading state to track the fetching
  const modalRef = useRef();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(orgList.length / itemsPerPage);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUid(user.uid);
      } else {
        setUserUid(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (userUid) {
      setLoading(true);  // Set loading to true when data starts loading
      getUsersOrganizations(userUid).then((organizations) => {
        organizations.forEach((org) => {
          if (org.description === 'description was not added') {
            org.description = '';
          }
        });
        if (organizations) {
          setOrgList(organizations);
          loadQueuesCounts(organizations);
        }
        setLoading(false);  // Set loading to false when data has been fetched
      });
    }
  }, [userUid]);

  const loadQueuesCounts = async (organizations) => {
    const counts = {};
    await Promise.all(
      organizations.map(async (org) => {
        const queues = await getOrganizationsQueues(org.id);
        counts[org.id] = queues ? Object.values(queues).length : 0;
      })
    );
    setQueuesCounts(counts);
  };

  const cancelEditing = (name, descr, color, logoImg) => {
    setNameInput(name);
    setDescriptionInput(descr);
    setIfEditMode(false);
    setColorInput(color);
    setEnableImgUpload(false);
    setLogo(logoImg);
  };

  const onDetailsHandler = (id, name, descr, color, logo) => {
    setExpanded(expanded === id ? null : id);
    if (ifEditMode) {
      cancelEditing(name, descr, color, logo);
    }
  };

  const onEditHandler = (orgId, name, descr, color, logo) => {
    setExpanded(orgId);
    setIfEditMode(true);
    setNameInput(name);
    setColorInput(color);
    setDescriptionInput(descr);
    setEnableImgUpload(true);
    setLogo(logo);
  };

  const onSaveHandler = async (id, changes) => {
    await updateUsersOrganization(id, changes);
    const updatedOrgs = await getUsersOrganizations(userUid);
    if (updatedOrgs) {
      setOrgList(updatedOrgs);
      loadQueuesCounts(updatedOrgs);
    }
    setNameInput('');
    setDescriptionInput('');
    setColorInput('#fff');
    setEnableImgUpload(false);
    setIfEditMode(false);
    setLogo(logo || '');
  };
  // Handle changes in logo
  const onSetLogoHandler = (logoString) => {
    setLogo(logoString);
  };

  const onRemoveLogoHandler = () => {
    setLogo('');
  };

  const handleClick = (event, orgId) => {
    setMenuAnchor((prev) => ({ ...prev, [orgId]: event.currentTarget }));
  };

  const handleClose = (orgId) => {
    setMenuAnchor((prev) => ({ ...prev, [orgId]: null }));
  };

  const openModal = (orgId) => {
    if (modalRef.current) {
      const orgName = orgList.filter((org) => org.id === orgId)[0].orgName;
      modalRef.current.openModal({
        id: orgId,
        text: `Are you sure you want to delete ${orgName || 'current item'} `,
      });
    } else {
      console.error('modalRef.current is null');
    }
  };
  const onDeletConfermedHandler = async (orgId) => {
    try {
      await deleteOrganization(orgId);
      const organizations = await getUsersOrganizations(userUid);
      setOrgList(organizations);
      // After deleting an organization, check if we need to go to the previous page
      const totalPages = Math.ceil(organizations.length / itemsPerPage);
      if (currentPage > totalPages) {
        setCurrentPage(totalPages); // Go to the last available page
      }
      handleClose(orgId);
    } catch (e) {
      console.error(e);
    }
  };

  if(!userUid){
    return(
      <Toast
      title="Temporary warning!"
      subTitle="Please sign up to work on this page."
    />
    )
  }

  return (
    <>
      <ModalConfirm
        ref={modalRef}
        onConfirm={onDeletConfermedHandler}
        onClose={handleClose}
        title="Delete item"
      ></ModalConfirm>

      {/* Conditionally render spinner or content */}
      {loading ? (
        <div className={classes.spinnerWrapper}>
          <Spinner animation="border" className={classes.customSpinner} />
          <p>Loading Organizations...</p>
        </div>
      ) : orgList.length === 0 ? (
        <div className={classes.noOrganizations}>
          <FaInbox className={classes.emptyIcon} />
          <p>No Organizations in the list</p>
          <button
            data-testid="orgs-list-create-org-btn"
            className="button"
            onClick={() => navigate('/dashboard/createorganization')}
          >
            Create Organization
          </button>
        </div>
      ) : (
        <>
          <h4>Organization List</h4>
          <div className="button-container">
            <div className="hide-on-mobile">
              <StandardButton
                className="button"
                onClick={() => navigate('/dashboard/createorganization')}
              >
                Create Organization
              </StandardButton>
            </div>
            {/* Show icon on mobile */}
            <NavLink
              data-testid="orgs-list-create-org-btn-mobile"
              to="/dashboard/createorganization"
              className="show-on-mobile"
            >
              <MdDomainAdd />
              <span>Create Org</span>
            </NavLink>
          </div>
          <Table hover className={classes.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Queues</th>
                <th style={{ width: 0 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgList
                .slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                )
                .map((org, index) => (
                  <Fragment key={org.id}>
                    <tr
                      className={
                        (index + 1) % 2 === 0 ? classes.evenRow : classes.oddRow
                      }
                    >
                      <td className={classes.tData}>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className={classes.tData}>{org.orgName}</td>
                      <td className={classes.queueData}>
                        {queuesCounts[org.id] ?? 'Loading...'}
                      </td>
                      <td className={classes.actionsRow}>
                        <div className={classes.mobileMenu}>
                          <IconButton
                            onClick={(event) => handleClick(event, org.id)}
                            size="small"
                            sx={{ ml: 2 }}
                            aria-controls={
                              menuAnchor[org.id] ? 'account-menu' : undefined
                            }
                            aria-haspopup="true"
                            aria-expanded={
                              menuAnchor[org.id] ? 'true' : undefined
                            }
                          >
                            <MoreVert />
                          </IconButton>

                          <Menu
                            anchorEl={menuAnchor[org.id]}
                            open={Boolean(menuAnchor[org.id])}
                            onClose={() => handleClose(org.id)}
                          >
                            <MenuItem
                              className={classes.menuItem}
                              onClick={() => {
                                onDetailsHandler(
                                  org.id,
                                  org.orgName,
                                  org.description,
                                  org.brandColor,
                                  org.logoImg
                                );
                                handleClose(org.id);
                              }}
                            >
                              {expanded === org.id ? (
                                <span>Hide details</span>
                              ) : (
                                <span>Show details</span>
                              )}
                            </MenuItem>
                            <MenuItem
                              className={classes.menuItem}
                              onClick={() => {
                                onEditHandler(
                                  org.id,
                                  org.orgName,
                                  org.description,
                                  org.brandColor,
                                  org.logoImg
                                );
                                handleClose(org.id);
                              }}
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              className={classes.menuItem}
                              onClick={() => openModal(org.id)}
                            >
                              Delete
                            </MenuItem>
                          </Menu>
                        </div>
                        <div className={classes.standartButtonsWrap}>
                          <StandardButton
                            className={classes.expandButton}
                            onClick={() =>
                              onDetailsHandler(
                                org.id,
                                org.orgName,
                                org.description,
                                org.brandColor,
                                org.logoImg
                              )
                            }
                          >
                            {expanded === org.id ? 'Collapse' : 'Expand'}
                          </StandardButton>

                          <StandardButton
                            onClick={() =>
                              onEditHandler(
                                org.id,
                                org.orgName,
                                org.description,
                                org.brandColor,
                                org.logoImg
                              )
                            }
                          >
                            Edit
                          </StandardButton>
                          <StandardButton
                            onClick={() => {
                              openModal(org.id);
                            }}
                          >
                            Delete
                          </StandardButton>
                        </div>
                      </td>
                    </tr>
                    {expanded === org.id && (
                      <tr
                        className={
                          (index + 1) % 2 === 0
                            ? classes.evenRow
                            : classes.oddRow
                        }
                      >
                        <td></td>
                        <td colSpan="5">
                          <div className={classes.details}>
                            <div>
                              <div className={classes.logoBox}>
                                {/* Display the logo if available */}
                                {org.logoImg || logo ? (
                                  <img
                                    src={
                                      !enableImgUpload
                                        ? org.logoImg
                                        : enableImgUpload && logo
                                        ? logo
                                        : '/images/nologo.png'
                                    }
                                    alt="Organization Logo"
                                    width="100"
                                    height="100"
                                  />
                                ) : (
                                  <img
                                    src="/images/nologo.png"
                                    alt="logo"
                                    width="100"
                                    height="100"
                                  />
                                )}
                                {enableImgUpload && (
                                  <div className={classes['logoOverlay']}>
                                    <FileUploader
                                      nameInput={nameInput}
                                      onSetLogoHandler={onSetLogoHandler}
                                    ></FileUploader>
                                  </div>
                                )}
                              </div>
                              {enableImgUpload && (
                                <span
                                  className={classes['removeBtn']}
                                  onClick={onRemoveLogoHandler}
                                >
                                  clear logo
                                </span>
                              )}
                            </div>

                            <div className={classes.descriptionText}>
                              <p>
                                <span>Name:</span>
                                {ifEditMode ? (
                                  <input
                                    onChange={(e) =>
                                      setNameInput(e.target.value)
                                    }
                                    value={nameInput}
                                    placeholder={org.orgName}
                                  />
                                ) : (
                                  org.orgName
                                )}
                              </p>
                              <p>
                                <span>Description:</span>
                                {!ifEditMode &&
                                  (!org.description ||
                                  org.description ===
                                    'description was not added'
                                    ? '-'
                                    : org.description)}
                                {ifEditMode && (
                                  <input
                                    onChange={(e) =>
                                      setDescriptionInput(e.target.value)
                                    }
                                    value={descriptionInput}
                                    placeholder={
                                      !org.description
                                        ? 'description was not added'
                                        : org.description
                                    }
                                  />
                                )}
                              </p>
                              {ifEditMode && (
                                <p className={classes.buttonsBlock}>
                                  <StandardButton
                                    onClick={() =>
                                      cancelEditing(
                                        org.orgName,
                                        org.desctiption,
                                        org.brandColor,
                                        org.logoImg
                                      )
                                    }
                                  >
                                    Cancel
                                  </StandardButton>
                                  <StandardButton
                                    onClick={() =>
                                      onSaveHandler(org.id, {
                                        orgName: nameInput,
                                        description: descriptionInput || '',
                                        brandColor: colorInput,
                                        logoImg: logo || '',
                                      })
                                    }
                                  >
                                    Save
                                  </StandardButton>
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
            </tbody>
          </Table>
          <div className="pagination-controls">
            <StandardButton
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </StandardButton>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <StandardButton
              onClick={() =>
                setCurrentPage((prev) =>
                  prev < Math.ceil(orgList.length / itemsPerPage)
                    ? prev + 1
                    : prev
                )
              }
              disabled={currentPage >= Math.ceil(orgList.length / itemsPerPage)}
            >
              Next
            </StandardButton>
          </div>
        </>
      )}
    </>
  );
}

export default OrganizationsList;
